import {FormControl, FormField} from 'platform/components';
import {Grid, GridItem, Heading, VStack} from 'platform/foundation';

import {
  WarrantyTypeCode,
  WarrantyTypeCodeSchema,
  WarrantyTypeSource,
  WarrantyTypeSourceSchema,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {SettingsSection} from '@dms/settings/shared';

import {WarrantyTypeFormType} from './WarrantyTypeFormSchema';

const WARRANTY_TYPE_LABEL_KEYS: Record<WarrantyTypeCode, string> = {
  MECHANICAL: 'entity.warrantyType.type.mechanical',
  PAINT_AND_SURFACE_CORROSION: 'entity.warrantyType.type.paintAndSurfaceCorrosion',
  ANTI_PERFORATION: 'entity.warrantyType.type.antiPerforation',
  HYBRID: 'entity.warrantyType.type.hybrid',
  HV_BATTERY_LIMITED: 'entity.warrantyType.type.hvBatteryLimited',
  HV_BATTERY_CAPACITY_LIMITED: 'entity.warrantyType.type.hvBatteryCapacityLimited',
  POWERTRAIN: 'entity.warrantyType.type.powertrain',
  EXTENDED_WARRANTY: 'entity.warrantyType.type.extendedWarranty',
  GAP_INSURANCE: 'entity.warrantyType.type.gapInsurance',
  TIRE_AND_WHEEL: 'entity.warrantyType.type.tireAndWheel',
  ACCESSORIES: 'entity.warrantyType.type.accessories',
  OTHER: 'entity.warrantyType.type.other',
};

const WARRANTY_SOURCE_LABEL_KEYS: Record<WarrantyTypeSource, string> = {
  OEM: 'entity.warrantyType.source.oem',
  DEALER: 'entity.warrantyType.source.dealer',
};

const getWarrantyTypeOptions = () =>
  WarrantyTypeCodeSchema.options.map((code) => ({
    label: i18n.t(WARRANTY_TYPE_LABEL_KEYS[code]),
    value: code,
  }));

const getWarrantySourceOptions = () =>
  WarrantyTypeSourceSchema.options.map((source) => ({
    label: i18n.t(WARRANTY_SOURCE_LABEL_KEYS[source]),
    value: source,
  }));

interface WarrantyTypeFormProps {
  control: FormControl<WarrantyTypeFormType>;
}

export function WarrantyTypeForm(props: WarrantyTypeFormProps) {
  return (
    <SettingsSection data-testid={testIds.settings.warrantyTypesDetail('form')}>
      <VStack spacing={4}>
        <Grid columns={2} spacing={4}>
          <GridItem span={2}>
            <FormField
              control={props.control}
              type="text"
              name="description"
              label={i18n.t('general.labels.description')}
              isRequired
              data-testid={testIds.settings.warrantyTypesDetail('description')}
            />
          </GridItem>
          <FormField
            control={props.control}
            type="choice"
            name="warrantyType"
            label={i18n.t('entity.warrantyType.labels.warrantyType')}
            options={getWarrantyTypeOptions()}
            isRequired
            data-testid={testIds.settings.warrantyTypesDetail('warrantyType')}
          />
          <FormField
            control={props.control}
            type="choice"
            name="source"
            label={i18n.t('general.labels.source')}
            options={getWarrantySourceOptions()}
            isRequired
            data-testid={testIds.settings.warrantyTypesDetail('source')}
          />
          <FormField
            control={props.control}
            type="text"
            name="externalId"
            label={i18n.t('entity.vehicle.labels.externalId')}
            data-testid={testIds.settings.warrantyTypesDetail('externalId')}
          />
        </Grid>
        <VStack spacing={2}>
          <Heading size={5}>{i18n.t('entity.warrantyType.labels.duration')}</Heading>
          <Grid columns={2} spacing={4}>
            <FormField
              control={props.control}
              type="integer"
              name="durationMonths"
              label={i18n.t('entity.warrantyType.labels.durationMonths')}
              suffix={i18n.t('general.labels.months')}
              data-testid={testIds.settings.warrantyTypesDetail('durationMonths')}
            />
            <FormField
              control={props.control}
              type="integer"
              name="durationKm"
              label={i18n.t('entity.warrantyType.labels.durationKm')}
              suffix={i18n.t('general.metric.km')}
              data-testid={testIds.settings.warrantyTypesDetail('durationKm')}
            />
          </Grid>
        </VStack>
      </VStack>
    </SettingsSection>
  );
}
